import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Info } from "lucide-react";
import { fetchCategories } from "../../api/categoryService";
import {
  fetchImageProposals,
  voteImageProposal,
} from "../../api/communityService";

function formatDate(value) {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function formatRemaining(value) {
  if (!value) return "Fin de vote non planifiee";
  const end = new Date(value).getTime();
  const now = Date.now();
  const remainingMs = end - now;

  if (remainingMs <= 0) {
    return "Vote termine prochainement";
  }

  const days = Math.floor(remainingMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((remainingMs / (1000 * 60 * 60)) % 24);

  if (days > 0) {
    return `${days} j ${hours} h restantes`;
  }

  return `${Math.max(1, hours)} h restantes`;
}

export default function PlayerSuggestions() {
  const [proposals, setProposals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    setError("");

    Promise.all([
      fetchImageProposals(controller.signal),
      fetchCategories(controller.signal),
    ])
      .then(([proposalData, categoryData]) => {
        setProposals(
          Array.isArray(proposalData?.proposals) ? proposalData.proposals : []
        );
        setCategories(Array.isArray(categoryData) ? categoryData : []);
      })
      .catch(() => {
        setError("Chargement des propositions impossible.");
      })
      .finally(() => setIsLoading(false));

    return () => controller.abort();
  }, []);

  const votingProposals = useMemo(
    () =>
      proposals
        .filter((proposal) => proposal.status === "voting")
        .filter((proposal) =>
          selectedCategoryId
            ? String(proposal.category_id) === String(selectedCategoryId)
            : true
        )
        .sort((first, second) => {
          const firstDate = new Date(
            first.vote_started_at || first.processed_at || first.created_at || 0
          ).getTime();
          const secondDate = new Date(
            second.vote_started_at || second.processed_at || second.created_at || 0
          ).getTime();
          return secondDate - firstDate;
        }),
    [proposals, selectedCategoryId]
  );

  const selectedCategory = useMemo(
    () =>
      categories.find(
        (category) => String(category.id) === String(selectedCategoryId)
      ) || null,
    [categories, selectedCategoryId]
  );

  const rulesTooltipText = selectedCategoryId
    ? selectedCategory?.proposal_rules ||
      "Aucune regle specifique n'est renseignee pour cette categorie."
    : "Selectionnez une categorie pour voir les regles.";

  const submitVote = async (proposalId, value) => {
    const proposal = proposals.find((item) => item.id === proposalId);
    if (proposal && proposal.can_vote === false) {
      setError(
        proposal.has_voted
          ? "Votre vote est déjà pris en compte."
          : "Vous ne pouvez pas voter pour votre propre proposition."
      );
      return;
    }

    setError("");
    try {
      const response = await voteImageProposal(proposalId, value);
      setProposals((current) =>
        current.map((proposal) =>
          proposal.id === proposalId ? response.proposal : proposal
        )
      );
    } catch (voteError) {
      setError(voteError instanceof Error ? voteError.message : "Vote impossible.");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto grid max-w-7xl gap-6 px-4 py-10 sm:px-6 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-end lg:px-8">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-sky-600">
              Vote communautaire
            </p>
            <h1 className="mt-3 text-3xl font-bold text-slate-950 sm:text-4xl">
              Photothèque
            </h1>
            <p className="mt-3 text-base text-slate-600">
              Retrouve les images approuvées par l'admin et encore ouvertes aux votes.
            </p>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex items-center justify-between gap-3">
              <label
                htmlFor="player-suggestions-category"
                className="text-sm font-semibold text-slate-700"
              >
                Filtrer par catégorie
              </label>
              <div className="group relative">
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-sky-700 shadow-sm ring-1 ring-sky-100 transition hover:bg-sky-100"
                  aria-label="Voir les regles de proposition"
                >
                  <Info className="h-4 w-4" />
                </button>
                <div className="pointer-events-none absolute right-0 top-10 z-20 hidden w-72 rounded-2xl border border-sky-100 bg-white p-4 text-sm leading-6 text-slate-600 shadow-xl group-hover:block group-focus-within:block">
                  <p className="whitespace-pre-wrap">{rulesTooltipText}</p>
                </div>
              </div>
            </div>
            <select
              id="player-suggestions-category"
              value={selectedCategoryId}
              onChange={(event) => setSelectedCategoryId(event.target.value)}
              className="mt-3 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
            >
              <option value="">Toutes les catégories</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            <Link
              to="/profile/image-proposals#proposition-image"
              className="mt-4 inline-flex rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
            >
              Proposer une image
            </Link>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {error ? (
          <div className="mb-6 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        ) : null}

        {isLoading ? (
          <div className="rounded-2xl border border-slate-200 bg-white px-4 py-8 text-sm text-slate-500">
            Chargement des propositions...
          </div>
        ) : votingProposals.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-10 text-center text-sm text-slate-500">
            Aucune proposition approuvée n'est ouverte au vote pour le moment.
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
            {votingProposals.map((proposal) => (
              <article
                key={proposal.id}
                className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
              >
                {proposal.image_url ? (
                  <img
                    src={proposal.image_url}
                    alt={proposal.answer}
                    className="h-52 w-full object-cover"
                  />
                ) : (
                  <div className="flex h-52 items-center justify-center bg-slate-100 text-sm text-slate-400">
                    Image indisponible
                  </div>
                )}

                <div className="space-y-4 p-4">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                      {proposal.category_name}
                    </div>
                    <h2 className="mt-1 text-lg font-bold text-slate-950">
                      {proposal.answer}
                    </h2>
                    <div className="mt-2 text-xs text-slate-500">
                      Mise en ligne :{" "}
                      {formatDate(
                        proposal.vote_started_at ||
                          proposal.processed_at ||
                          proposal.created_at
                      )}
                    </div>
                    <div className="mt-1 text-xs font-semibold text-sky-700">
                      {formatRemaining(proposal.auto_review_at)}
                    </div>
                  </div>

                  {proposal.source_url ? (
                    <a
                      href={proposal.source_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex text-xs font-semibold text-sky-700 hover:text-sky-900"
                    >
                      Source de l'image
                    </a>
                  ) : null}

                  <div className="flex gap-2">
                    {proposal.can_vote === false ? (
                      <div className="flex-1 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-600">
                        {proposal.has_voted
                          ? "Votre vote est déjà pris en compte."
                          : "Vous ne pouvez pas voter pour votre propre proposition."}
                      </div>
                    ) : (
                      <>
                    <button
                      type="button"
                      onClick={() => submitVote(proposal.id, 1)}
                      className={`flex-1 rounded-full px-4 py-2 text-sm font-semibold transition ${
                        proposal.current_vote === 1
                          ? "bg-emerald-500 text-white"
                          : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                      }`}
                    >
                      Oui {proposal.upvotes}
                    </button>
                    <button
                      type="button"
                      onClick={() => submitVote(proposal.id, -1)}
                      className={`flex-1 rounded-full px-4 py-2 text-sm font-semibold transition ${
                        proposal.current_vote === -1
                          ? "bg-rose-500 text-white"
                          : "bg-rose-50 text-rose-700 hover:bg-rose-100"
                      }`}
                    >
                      Non {proposal.downvotes}
                    </button>
                      </>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
